import StatusPill from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime, formatNumber } from '@/lib/format';
import MessageStatusPill from '@/pages/apps/components/message-status-pill';
import NextStepsCard from '@/pages/apps/components/next-steps-card';
import SettingsCard from '@/pages/apps/components/settings-card';
import SetupCard from '@/pages/apps/components/setup-card';
import SetupStatusPill from '@/pages/apps/components/setup-status-pill';
import {
  app_drafts_path,
  app_invoices_path,
  app_list_records_path,
  app_messages_path,
  app_publication_path,
} from '@/rails/routes';
import type { AppShell, Connection, Message, Provisioning } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { useEffect } from 'react';

interface ListOverview {
  key: string;
  name: string;
  singular: string;
  fieldCount: number;
  recordCount: number;
}

interface AppsShowProps {
  app: AppShell;
  provisioning: Provisioning | null;
  lists: ListOverview[];
  schemaCurrent: boolean;
  readyDrafts: number;
  // True while a described design is still being written.
  drafting: boolean;
  connections: Connection[];
  invoices: { openCount: number; paidCount: number; draftCount: number };
  messages: Message[];
  canManage: boolean;
}

const POLL_EVERY_MS = 3000;
// What setup and drafting can change while they run: setup's own state, the
// app, whether the design is applied, and whether a draft has come back.
const SETUP_PROPS = ['provisioning', 'app', 'schemaCurrent', 'readyDrafts', 'drafting'];

const AppsShow = ({
  app,
  provisioning,
  lists,
  schemaCurrent,
  readyDrafts,
  drafting,
  connections,
  invoices,
  messages,
  canManage,
}: AppsShowProps) => {
  const settingUp = provisioning?.status === 'pending' || provisioning?.status === 'running';
  const waiting = settingUp || drafting;

  // Setup and drafting run in the background. Ask for their state until both settle, then stop.
  useEffect(() => {
    if (!waiting) return undefined;

    const timer = setInterval(() => router.reload({ only: SETUP_PROPS }), POLL_EVERY_MS);

    return () => clearInterval(timer);
  }, [waiting]);

  const invoicesPath = app_invoices_path(app.id);
  const invoiceCounts = [
    { label: 'Open', count: invoices.openCount },
    { label: 'Paid', count: invoices.paidCount },
    { label: 'Draft', count: invoices.draftCount },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={app.name}
        description={app.description ?? undefined}
        badges={provisioning ? <SetupStatusPill status={provisioning.status} /> : null}
      />

      {app.published ? (
        <Card>
          <CardContent className="space-y-2 text-sm">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <StatusPill tone="success">Published</StatusPill>
              {app.unpublishedChanges ? (
                <StatusPill tone="warning">Unpublished changes</StatusPill>
              ) : null}
              <span className="text-muted-foreground">Public address</span>
              {app.publicUrl ? (
                <a
                  href={app.publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium break-all underline"
                >
                  {app.publicUrl}
                </a>
              ) : null}
            </p>
            {app.unpublishedChanges ? (
              <p>
                The design has changed since you published. The public app still shows the earlier
                one.{' '}
                <Link href={app_publication_path(app.id)} className="font-medium underline">
                  Go to publish
                </Link>
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2">
        <SetupCard appId={app.id} provisioning={provisioning} />
        <NextStepsCard
          app={app}
          listCount={lists.length}
          schemaCurrent={schemaCurrent}
          readyDrafts={readyDrafts}
          drafting={drafting}
          connections={connections}
          settingUp={settingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Lists</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lists.length === 0 && drafting ? (
            <EmptyState
              title="Your design is being drafted…"
              description="You review the draft before it becomes your app's lists."
            />
          ) : lists.length === 0 ? (
            <EmptyState
              title="No lists yet"
              description="A list holds one kind of record, like jobs or clients. The design decides which lists your app has."
              action={
                <Button asChild variant="outline">
                  <Link href={app_drafts_path(app.id)}>Design with AI</Link>
                </Button>
              }
            />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {lists.map((list) => (
                <li key={list.key} className="min-w-0">
                  <Link
                    href={app_list_records_path(app.id, list.key)}
                    className="hover:bg-muted/40 block rounded-lg border p-4 transition-colors"
                  >
                    <span className="block truncate text-sm font-medium">{list.name}</span>
                    <span className="mt-2 block text-2xl font-semibold tracking-tight tabular-nums">
                      {formatNumber(list.recordCount)}
                    </span>
                    <span className="text-muted-foreground block text-xs">
                      {list.recordCount === 1 ? 'record' : 'records'} ·{' '}
                      {formatNumber(list.fieldCount)} {list.fieldCount === 1 ? 'field' : 'fields'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>
              <h2>Invoices</h2>
            </CardTitle>
            <CardAction>
              <Link href={invoicesPath} className="text-sm font-medium underline">
                All invoices
              </Link>
            </CardAction>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-3 gap-3">
              {invoiceCounts.map((item) => (
                <li key={item.label} className="min-w-0">
                  <Link
                    href={invoicesPath}
                    className="hover:bg-muted/40 block rounded-lg border p-3 transition-colors"
                  >
                    <span className="text-muted-foreground block text-xs font-medium tracking-wide uppercase">
                      {item.label}
                    </span>
                    <span className="mt-1 block text-2xl font-semibold tracking-tight tabular-nums">
                      {formatNumber(item.count)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>
              <h2>Recent messages</h2>
            </CardTitle>
            <CardAction>
              <Link href={app_messages_path(app.id)} className="text-sm font-medium underline">
                All messages
              </Link>
            </CardAction>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-muted-foreground text-sm">No email has gone out yet.</p>
            ) : (
              <ul className="divide-y">
                {messages.map((message) => (
                  <li key={message.id} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{message.subject}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        To {message.toAddress} ·{' '}
                        {formatDateTime(message.sentAt ?? message.createdAt)}
                      </p>
                    </div>
                    <MessageStatusPill status={message.status} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {canManage ? <SettingsCard app={app} /> : null}
    </div>
  );
};

export default AppsShow;
